import { useContext, useState } from "react";
import { Button, Input, Modal, RadioGroup, Paragraph, Select, useToast } from "@aidkitorg/component-library";
import { EnvelopeIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  validTimezones,
  type SummaryFrequency,
  type Timezone,
} from "@aidkitorg/types/lib/survey";
import {
  DEFAULT_SUMMARY_DAY_OF_WEEK,
  DEFAULT_SUMMARY_SEND_HOUR,
  DEFAULT_SUMMARY_TIMEZONE,
  daysOfWeek,
  type DayOfWeek,
} from "@aidkitorg/types/lib/utils/time";
import type { Translations } from "@aidkitorg/i18n/lib";
import { usePost } from "../../API";
import { ConfigurationContext } from "../../Context";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { useLocalizedStrings } from "../../Localization";
import { hourLabel, withEntry, type EntryError } from "./reviewSummaryFields";

/**
 * The cadence the radio group picks, which is the frequency's discriminant.
 *
 * The form holds the cadence and the day as separate controls — that's what lets a switch to Daily
 * and back keep the day the user had chosen — and derives the {@link SummaryFrequency} it saves. A
 * daily cadence has nowhere to put a day, so the payload can't carry a meaningless one.
 */
type Cadence = SummaryFrequency['kind'];

/** The strings this modal reads, which the option lists need too. */
type SummaryStrings = Translations['ai_coach']['summary_settings'];

// Functions rather than constants because the labels come from a hook, which can't be called out
// here.
function frequencyOptions(strings: SummaryStrings) {
  return [
    { value: 'daily', label: strings.daily, description: strings.daily_description },
    { value: 'weekly', label: strings.weekly, description: strings.weekly_description },
  ];
}

// The day is its own English name in the survey, so the label is a lookup rather than the value.
function dayOptions(strings: SummaryStrings) {
  return daysOfWeek.map((day) => ({ value: day, label: strings.days_of_week[day] }));
}

// Stored as 0-23, but shown the way someone would say it. A select rather than a number input, so
// the hour can't land out of range from here.
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({ value: hour, label: hourLabel(hour) }));

/** "America/New_York" reads as "New York" once the region prefix is dropped. */
function timezoneLabel(timezone: Timezone): string {
  return timezone === 'UTC' ? 'UTC' : timezone.split('/')[1].replace(/_/g, ' ');
}

const TIMEZONE_OPTIONS = validTimezones.map((timezone) => ({
  value: timezone,
  label: timezoneLabel(timezone),
}));

/**
 * Recurring-summary settings for the conversation review queue. Saving publishes to the survey, so
 * this is limited to the roles that are allowed to do that; plain reviewers don't see the control.
 */
export function ReviewSummarySettings() {
  const configuration = useContext(ConfigurationContext);
  const L = useLocalizedStrings();
  const canEdit = ['admin', 'ai_reviewer'].some((role) => (configuration.roles || []).includes(role));

  const [open, setOpen] = useState(false);

  if (!canEdit) {
    return null;
  }

  return <>
    <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
      <span className="flex items-center gap-1.5">
        <EnvelopeIcon className="h-4 w-4" />
        {L.ai_coach.summary_settings.open}
      </span>
    </Button>
    {/* Mounted only while open so each visit re-fetches, which is also what makes Cancel discard. */}
    {open && <ReviewSummaryModal onClose={() => setOpen(false)} />}
  </>;
}

function ReviewSummaryModal({ onClose }: { onClose: () => void }) {
  const getConfig = usePost('/ai/review_summary/get');
  const updateConfig = usePost('/ai/review_summary/update');
  const { toast } = useToast();
  const strings = useLocalizedStrings().ai_coach.summary_settings;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cadence, setCadence] = useState<Cadence>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState<DayOfWeek>(DEFAULT_SUMMARY_DAY_OF_WEEK);
  const [hour, setHour] = useState<number>(DEFAULT_SUMMARY_SEND_HOUR);
  const [timezone, setTimezone] = useState<Timezone>(DEFAULT_SUMMARY_TIMEZONE);
  const [recipients, setRecipients] = useState<string[]>([]);
  // Paused in Distro. The modal can't toggle it, but it should say so rather than imply mail is
  // going out.
  const [paused, setPaused] = useState(false);
  const [entry, setEntry] = useState('');
  // The reason, not the sentence, so the message stays in the language the modal is rendering in
  // rather than the one it was in when the entry was rejected.
  const [entryError, setEntryError] = useState<EntryError>();
  // A failed load must not fall through to the empty defaults: Save would publish those over a real
  // config and wipe its recipients and schedule.
  const [loadFailed, setLoadFailed] = useState(false);

  useAsyncEffect(async () => {
    const response = await getConfig({});
    // Runtime check, not a type-level one: a failed request resolves to `{ error }` from the route
    // middleware, which the endpoint's return type doesn't describe.
    if (!('config' in response)) {
      setLoadFailed(true);
      setLoading(false);
      return;
    }
    // An unset timezone is inherited rather than absent, so show the one actually in effect. Day and
    // hour just fall back to the values the cron would use.
    setTimezone(response.config?.timezone ?? response.inheritedTimezone);
    if (response.config) {
      const { frequency } = response.config;
      setCadence(frequency.kind);
      // A daily config has no day of its own, so the picker starts on the default it would get by
      // switching to weekly.
      setDayOfWeek(
        (frequency.kind === 'weekly' ? frequency.dayOfWeek : undefined) ?? DEFAULT_SUMMARY_DAY_OF_WEEK
      );
      setHour(response.config.hour ?? DEFAULT_SUMMARY_SEND_HOUR);
      setRecipients((response.config.recipients ?? []).map(({ emailAddress }) => emailAddress));
      setPaused(!!response.config.disabled);
    }
    setLoading(false);
  }, []);

  function addEntry() {
    const next = withEntry(recipients, entry);
    if ('error' in next) {
      setEntryError(next.error);
      return;
    }
    setRecipients(next.recipients);
    setEntry('');
    setEntryError(undefined);
  }

  async function save() {
    // An address still sitting in the input is the obvious way to lose one, so save it too rather
    // than making Add mandatory.
    const next = withEntry(recipients, entry);
    if ('error' in next) {
      setEntryError(next.error);
      return;
    }

    setSaving(true);
    try {
      const frequency: SummaryFrequency = cadence === 'weekly'
        ? { kind: 'weekly', dayOfWeek }
        : { kind: 'daily' };

      const response = await updateConfig({
        frequency,
        recipients: next.recipients,
        hour,
        timezone,
      });

      // A failure resolves to `{ error }` from the route middleware, which usePost has already
      // toasted, so only the success path is handled here.
      if ('config' in response) {
        toast({ description: strings.saved, variant: 'success' });
        onClose();
      }
    } finally {
      // In a finally so a thrown request doesn't leave the form stuck mid-save.
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={strings.title}
      titleSize="h2"
    >
      <div className="flex flex-col gap-5 w-full">
        <Paragraph size="sm" className="text-gray-600">
          {strings.intro}
        </Paragraph>

        {loading
          ? <Paragraph size="sm">{strings.loading}</Paragraph>
          : loadFailed
            // No form at all on a failed load: an empty one invites a Save that would overwrite the
            // settings we couldn't read. usePost has already toasted the reason.
            ? <div className="flex flex-col gap-4">
              <Paragraph size="sm" className="text-red-700">
                {strings.load_failed}
              </Paragraph>
              <div className="flex justify-end">
                <Button variant="secondary" onClick={onClose}>{strings.close}</Button>
              </div>
            </div>
            : <>
              {paused && (
                <Paragraph size="sm" className="text-amber-700">
                  {strings.paused}
                </Paragraph>
              )}

              <RadioGroup
                label={strings.send_summary}
                variant="tile"
                options={frequencyOptions(strings)}
                value={cadence}
                onChange={(value) => setCadence(value as Cadence)}
              />

              <div className="flex flex-wrap items-start gap-3">
                {/* Only the weekly variant carries a day, so a daily cadence has none to show. */}
                {cadence === 'weekly' && (
                  <div className="min-w-[10rem]">
                    <Select
                      label={strings.day}
                      description=""
                      options={dayOptions(strings)}
                      value={{ value: dayOfWeek, label: strings.days_of_week[dayOfWeek] }}
                      onChange={(option) => setDayOfWeek(option.value)}
                    />
                  </div>
                )}
                <div className="min-w-[10rem]">
                  <Select
                    label={strings.time}
                    description=""
                    options={HOUR_OPTIONS}
                    value={{ value: hour, label: hourLabel(hour) }}
                    onChange={(option) => setHour(option.value)}
                  />
                </div>
                <div className="min-w-[10rem]">
                  <Select
                    label={strings.timezone}
                    description=""
                    options={TIMEZONE_OPTIONS}
                    value={{ value: timezone, label: timezoneLabel(timezone) }}
                    onChange={(option) => setTimezone(option.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2 w-full">
                <form
                  className="flex flex-row flex-1 gap-2"
                  onSubmit={(e) => { e.preventDefault(); addEntry(); }}
                >
                  <Input
                    wrapperClassName="flex-1 sm:w-full"
                    label={strings.recipients_label}
                    placeholder={strings.recipients_placeholder}
                    value={entry}
                    onChange={(e) => { setEntry(e.target.value); setEntryError(undefined); }}
                    error={entryError
                      ? { hasError: true, errorMessage: strings.entry_errors[entryError] }
                      : undefined}
                  />
                  <Button variant="secondary" className="self-end" type="submit">{strings.add}</Button>
                </form>

                {recipients.length === 0
                  ? <Paragraph size="sm" className="text-gray-500">
                    {strings.no_recipients}
                  </Paragraph>
                  : <ul className="flex flex-col gap-1">
                    {recipients.map((recipient) => (
                      <li
                        key={recipient}
                        className="flex items-center justify-between gap-2 rounded-md border border-gray-200 px-3 py-2"
                      >
                        <span className="text-sm break-all">{recipient}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={strings.remove_recipient.replace('$email', recipient)}
                          onClick={() => setRecipients(recipients.filter((r) => r !== recipient))}
                        >
                          <span className="flex items-center gap-1 text-red-600">
                            <TrashIcon className="h-4 w-4" />
                            {strings.remove}
                          </span>
                        </Button>
                      </li>
                    ))}
                  </ul>}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={onClose} isDisabled={saving}>{strings.cancel}</Button>
                {/* isDisabled, not just loading: Button styles the spinner off `loading` but only
                  blocks clicks off `isDisabled`, and a second click would publish twice. */}
                <Button variant="primary" onClick={save} loading={saving} isDisabled={saving}>{strings.save}</Button>
              </div>
            </>}
      </div>
    </Modal>
  );
}
